import { WEEKDAY_LABEL, WEEKDAYS, formatPhoneBR, normalizePhoneBR, telLink, waLink, } from "@farmacias/schema";
import { escapeHtml, html, raw, safeUrl } from "./escape.js";
import { renderStyles } from "./styles.js";
import { buildPharmacyJsonLd } from "./jsonld.js";
import { iconArrow, iconAward, iconCalendar, iconCheck, iconClock, iconInstagram, iconMail, iconPhone, iconPin, iconServico, iconShield, iconUsers, iconWhatsapp, } from "./icons.js";
const EMPTY = raw("");
/**
 * Metadados fixos da Onio Chat — iguais em toda landing, independente da
 * farmácia. `author` é quem opera a plataforma; `fb:app_id` é o app do
 * Facebook usado nas ferramentas de compartilhamento.
 */
const AUTHOR = "on.io chat";
const FB_APP_ID = "1910054412865833";
/* ------------------------------------------------------------------ *
 * Derivações — tudo que o layout mostra sem o suporte precisar digitar
 * ------------------------------------------------------------------ */
/** Endereço em duas linhas (rua/número/complemento · bairro/cidade/CEP). */
function addressLines(f) {
    const e = f.endereco;
    const l1 = [[e.logradouro, e.numero].filter(Boolean).join(", "), e.complemento]
        .filter(Boolean)
        .join(" · ");
    const l2 = [e.bairro, [e.cidade, e.uf].filter(Boolean).join(" – "), e.cep ? `CEP ${e.cep}` : ""]
        .filter(Boolean)
        .join(" · ");
    return [l1, l2].filter(Boolean);
}
function addressOneLine(f) {
    return addressLines(f).join(" · ");
}
function mapsQuery(f) {
    return encodeURIComponent(`${addressOneLine(f)} ${f.nomeFantasia}`.trim());
}
function mapsEmbedUrl(f) {
    return `https://www.google.com/maps?q=${mapsQuery(f)}&output=embed`;
}
/** Link de rota (abre o app de mapas no celular). */
function mapsLinkUrl(f) {
    return `https://www.google.com/maps/search/?api=1&query=${mapsQuery(f)}`;
}
const pad = (s) => s.replace(":", "h");
/** `08h00 às 22h00` — formato de destaque. */
const faixaLonga = (h) => `${pad(h.abre)} às ${pad(h.fecha)}`;
/** `08:00 – 22:00` — formato de tabela. */
const faixaCurta = (h) => `${h.abre} – ${h.fecha}`;
const FECHADO = "Fechado";
/**
 * Turnos de um dia, ordenados pela abertura. A ordenação é o que garante saída
 * estável: o painel grava os horários na ordem em que o suporte digitou, e sem
 * isso uma farmácia que fecha pro almoço anunciava o turno da tarde como se
 * fosse o horário de abertura.
 */
function turnosDoDia(f, dia) {
    return f.horarios
        .filter((h) => h.dia === dia)
        .sort((a, b) => a.abre.localeCompare(b.abre) || a.fecha.localeCompare(b.fecha));
}
/**
 * Agrupa dias seguidos com o mesmo horário — `Segunda a Sexta 08:00 – 22:00`
 * em vez de cinco linhas idênticas.
 *
 * Os sete dias da semana sempre entram: dia sem horário cadastrado vira
 * `Fechado`, porque omitir a linha deixava o cliente sem saber se a farmácia
 * não abre ou se o suporte esqueceu de preencher. `feriado` é exceção — só
 * aparece quando cadastrado, já que não faz parte do ciclo semanal e afirmar
 * "Feriados: Fechado" por omissão seria inventar informação.
 */
function gruposHorario(f) {
    const out = [];
    let atual = null;
    for (const d of SEMANA) {
        const turnos = turnosDoDia(f, d);
        const aberto = turnos.length > 0;
        const curto = aberto ? turnos.map(faixaCurta).join(" e ") : FECHADO;
        if (atual !== null && atual.curto === curto) {
            atual.dias.push(d);
        }
        else {
            atual = {
                label: "",
                dias: [d],
                turnos,
                aberto,
                curto,
                longo: aberto ? turnos.map(faixaLonga).join(" e ") : FECHADO,
            };
            out.push(atual);
        }
    }
    const feriado = turnosDoDia(f, "feriado");
    if (feriado.length) {
        out.push({
            label: "",
            dias: ["feriado"],
            turnos: feriado,
            aberto: true,
            curto: feriado.map(faixaCurta).join(" e "),
            longo: feriado.map(faixaLonga).join(" e "),
        });
    }
    for (const g of out) {
        const first = WEEKDAY_LABEL[g.dias[0]];
        const last = WEEKDAY_LABEL[g.dias[g.dias.length - 1]];
        g.label =
            g.dias.length === 1 ? first : g.dias.length === 2 ? `${first} e ${last}` : `${first} a ${last}`;
    }
    return out;
}
const SEMANA = WEEKDAYS.filter((d) => d !== "feriado");
/**
 * Grupo que cobre mais dias — vira o card flutuante do hero e a linha do
 * rodapé. Carrega `longo` com **todos** os turnos, então resumir nunca esconde
 * horário de funcionamento.
 */
function grupoPrincipal(grupos) {
    // Só grupos abertos: o teaser do hero e a linha do rodapé nunca podem
    // anunciar "Segunda a Sexta · Fechado" como se fosse o horário da farmácia.
    const semana = grupos.filter((g) => g.aberto && !g.dias.includes("feriado"));
    if (!semana.length)
        return null;
    const g = semana.reduce((a, b) => (b.dias.length > a.dias.length ? b : a));
    return SEMANA.every((d) => g.dias.includes(d)) ? { ...g, label: "Todos os dias" } : g;
}
function hojeSP() {
    try {
        const d = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Sao_Paulo",
            weekday: "short",
        }).format(new Date());
        const map = {
            Mon: "seg", Tue: "ter", Wed: "qua", Thu: "qui", Fri: "sex", Sat: "sab", Sun: "dom",
        };
        return map[d] ?? null;
    }
    catch {
        return null;
    }
}
/** Bloco "Por que escolher" — fatos reais derivados do cadastro, nunca copy. */
function fatos(f, principal) {
    const out = [];
    const temDom = f.horarios.some((h) => h.dia === "dom");
    const temFeriado = f.horarios.some((h) => h.dia === "feriado");
    if (f.farmaceuticos.length) {
        out.push({
            icone: iconUsers,
            texto: f.farmaceuticos.length > 1
                ? `${f.farmaceuticos.length} farmacêuticos responsáveis`
                : "Farmacêutico(a) responsável na loja",
        });
    }
    if (principal?.label === "Todos os dias") {
        out.push({ icone: iconCalendar, texto: "Aberta todos os dias da semana" });
    }
    else if (temDom && temFeriado) {
        out.push({ icone: iconCalendar, texto: "Aberta aos domingos e feriados" });
    }
    else if (temDom) {
        out.push({ icone: iconCalendar, texto: "Aberta aos domingos" });
    }
    if (f.whatsapp)
        out.push({ icone: iconWhatsapp, texto: "Pedidos e dúvidas pelo WhatsApp" });
    const local = f.endereco.bairro || f.endereco.cidade;
    if (local)
        out.push({ icone: iconPin, texto: `Fácil de chegar, no ${local}` });
    if (f.licencaSanitaria) {
        out.push({ icone: iconShield, texto: "Licença sanitária e AFE regulares" });
    }
    if (f.servicos.length >= 2) {
        out.push({ icone: iconAward, texto: `${f.servicos.length} serviços na loja` });
    }
    return out;
}
/** H1: trechos entre *asteriscos* saem na cor da marca. */
function headlineHtml(text) {
    return raw(escapeHtml(text)
        .split(/\*([^*]+)\*/g)
        .map((p, i) => (i % 2 ? `<span class="hl">${p}</span>` : p))
        .join(""));
}
function headline(f) {
    if (f.headline)
        return f.headline;
    const local = f.endereco.bairro || f.endereco.cidade;
    return local ? `Cuidando da *saúde* de quem é de ${local}.` : `Cuidando da *saúde* da sua família.`;
}
function lede(f) {
    if (f.descricaoCurta)
        return f.descricaoCurta;
    const local = [f.endereco.bairro, f.endereco.cidade].filter(Boolean).join(", ");
    return `Medicamentos, higiene, beleza e atendimento farmacêutico${local ? ` em ${local}` : ""}.`;
}
function metaDescription(f, name) {
    if (f.descricaoCurta)
        return f.descricaoCurta;
    const local = [f.endereco.bairro, f.endereco.cidade].filter(Boolean).join(", ");
    return `${name}${local ? ` — ${local}` : ""}. Atendimento pelo WhatsApp, endereço, horários e contato.`;
}
function paragrafos(texto) {
    return texto.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}
function fotoAlt(foto, fallback) {
    return foto.alt || fallback;
}
/* ------------------------------------------------------------------ *
 * Blocos
 * ------------------------------------------------------------------ */
function header(f, name, waHref, secoes) {
    const logoUrl = f.logo?.url ?? "";
    const links = [];
    if (secoes.has("sobre"))
        links.push(html `<a href="#sobre">Sobre</a>`);
    if (secoes.has("servicos"))
        links.push(html `<a href="#servicos">Serviços</a>`);
    links.push(html `<a href="#loja">Localização</a>`);
    if (secoes.has("contato"))
        links.push(html `<a href="#contato">Contato</a>`);
    return html `<header class="site-header">
    <div class="container">
      <a class="brand" href="/">
        ${logoUrl ? html `<img src="${safeUrl(logoUrl)}" alt="" width="120" height="38" />` : EMPTY}
        <span>${name}</span>
      </a>
      <nav class="nav" aria-label="Seções da página">${links}</nav>
      ${waHref
        ? html `<a class="btn btn--wa btn--sm" href="${safeUrl(waHref)}" rel="noopener" target="_blank">${iconWhatsapp}WhatsApp</a>`
        : EMPTY}
    </div>
  </header>`;
}
function hero(f, name, waHref, principal) {
    const hasPhone = normalizePhoneBR(f.telefoneComercial) !== null;
    const foto = f.fotoHero;
    const ctas = [];
    if (waHref) {
        ctas.push(html `<a class="btn btn--wa" href="${safeUrl(waHref)}" rel="noopener" target="_blank">${iconWhatsapp}Falar com um atendente</a>`);
    }
    if (hasPhone) {
        ctas.push(html `<a class="btn btn--ghost" href="${safeUrl(telLink(f.telefoneComercial))}">${iconPhone}${formatPhoneBR(f.telefoneComercial)}</a>`);
    }
    // A lista de checks é a copy do operador. Sem ela o hero fica só com o
    // essencial — os fatos derivados já aparecem no bloco "Por que escolher".
    const perks = f.diferenciais.filter(Boolean).slice(0, 6);
    const media = foto
        ? html `<div class="hero-media">
        <div class="frame">
          <img
            src="${safeUrl(foto.url)}"
            alt="${fotoAlt(foto, `Atendimento na ${name}`)}"
            width="800"
            height="640"
            fetchpriority="high"
          />
        </div>
        ${principal
            ? html `<div class="float-card">
              <span class="chip chip--sm">${iconClock}</span>
              <span>
                <b>${principal.label}</b>
                <small>${principal.longo}</small>
              </span>
            </div>`
            : EMPTY}
      </div>`
        : EMPTY;
    return html `<section class="hero" id="topo">
    <div class="container">
      <div class="hero-grid${foto ? "" : " hero-grid--solo"}">
        <div class="hero-copy">
          ${f.badge ? html `<p class="badge">${iconCheck}${f.badge}</p>` : EMPTY}
          <h1>${headlineHtml(headline(f))}</h1>
          <p class="lede">${lede(f)}</p>
          ${perks.length
        ? html `<ul class="perks">
                ${perks.map((p) => html `<li>${iconCheck}<span>${p}</span></li>`)}
              </ul>`
        : EMPTY}
          ${ctas.length ? html `<div class="hero-ctas">${ctas}</div>` : EMPTY}
        </div>
        ${media}
      </div>
    </div>
  </section>`;
}
function sobreSection(f, name) {
    const texto = f.sobre || f.descricaoCurta;
    if (!texto)
        return EMPTY;
    const local = f.endereco.cidade;
    return html `<section class="section" id="sobre">
    <div class="container">
      <div class="section-head section-head--center">
        <p class="eyebrow">Sobre a ${name}</p>
        <h2 class="section-title">
          ${local ? `Tradição, confiança e cuidado em ${local}.` : "Tradição, confiança e cuidado em cada atendimento."}
        </h2>
      </div>
      <div class="prose">${paragrafos(texto).map((p) => html `<p>${p}</p>`)}</div>
    </div>
  </section>`;
}
function servicosSection(f) {
    if (!f.servicos.length)
        return EMPTY;
    return html `<section class="section section--soft" id="servicos">
    <div class="container">
      <div class="section-head section-head--center">
        <p class="eyebrow">Nossos serviços</p>
        <h2 class="section-title">Tudo o que você precisa em um só lugar.</h2>
      </div>
      <div class="svc-grid">
        ${f.servicos.map((s) => html `<article class="svc">
            <span class="chip">${iconServico(s.icone)}</span>
            <h3>${s.titulo}</h3>
            ${s.descricao ? html `<p>${s.descricao}</p>` : EMPTY}
          </article>`)}
      </div>
    </div>
  </section>`;
}
function porQueSection(f, name, waHref, principal) {
    const itens = fatos(f, principal);
    if (itens.length < 3)
        return EMPTY;
    return html `<section class="section">
    <div class="container">
      <div class="why">
        <div>
          <p class="eyebrow">Por que escolher a ${name}?</p>
          <h2 class="section-title">Atendimento que faz a diferença.</h2>
          <p class="section-lede">
            Equipe preparada, orientação farmacêutica de verdade e a comodidade que a sua família merece.
          </p>
          ${waHref
        ? html `<div class="hero-ctas">
                <a class="btn btn--wa" href="${safeUrl(waHref)}" rel="noopener" target="_blank">${iconWhatsapp}Fale com nossa equipe</a>
              </div>`
        : EMPTY}
        </div>
        <div class="why-grid">
          ${itens.map((i) => html `<div class="why-item"><span class="chip chip--sm">${i.icone}</span><span>${i.texto}</span></div>`)}
        </div>
      </div>
    </div>
  </section>`;
}
function lojaSection(f, name, waHref, grupos, principal) {
    const hoje = hojeSP();
    const linhas = addressLines(f);
    const cidade = [f.endereco.cidade, f.endereco.uf].filter(Boolean).join(" – ");
    const foto = f.fotoFachada;
    const media = foto
        ? html `<figure class="photo-card">
        <img
          src="${safeUrl(foto.url)}"
          alt="${fotoAlt(foto, `Fachada da ${name}`)}"
          width="800"
          height="1000"
          loading="lazy"
        />
        <figcaption class="photo-overlay">
          ${cidade ? html `<span class="tag">${iconPin}${cidade}</span>` : EMPTY}
          ${linhas[0] ? html `<b>${linhas[0]}</b>` : EMPTY}
          ${f.endereco.cep ? html `<small>CEP ${f.endereco.cep}</small>` : EMPTY}
        </figcaption>
      </figure>`
        : html `<iframe
        class="map"
        title="Mapa — ${name}"
        src="${safeUrl(mapsEmbedUrl(f))}"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
      ></iframe>`;
    // O número grande da referência só entra quando existe um horário único de
    // verdade: um grupo de dias e um turno. Com dias variados ou com turno
    // partido (fecha pro almoço) a tabela é a única forma de não omitir horário.
    const destaque = grupos.length === 1 && principal !== null && principal.turnos.length === 1 ? principal : null;
    const linhaHorario = (g) => {
        const classes = [hoje && g.dias.includes(hoje) ? "today" : "", g.aberto ? "" : "fechado"]
            .filter(Boolean)
            .join(" ");
        return html `<tr${classes ? raw(` class="${classes}"`) : EMPTY}>
      <th scope="row">${g.label}</th>
      <td>${g.curto}</td>
    </tr>`;
    };
    // Sem nenhum dia aberto não há o que informar — sete linhas "Fechado" não
    // ajudam ninguém e provavelmente é rascunho sem horário preenchido.
    const cardHorario = grupos.some((g) => g.aberto)
        ? html `<div class="info-card">
        <span class="chip">${iconClock}</span>
        <p class="eyebrow">Horário de atendimento</p>
        ${destaque
            ? html `<b>${destaque.label}</b>
              <div class="big">${destaque.longo}</div>`
            : html `<table class="hours">
              <caption class="visually-hidden">Horário de funcionamento por dia</caption>
              <tbody>
                ${grupos.map(linhaHorario)}
              </tbody>
            </table>`}
      </div>`
        : EMPTY;
    return html `<section class="section section--soft" id="loja">
    <div class="container">
      <div class="section-head section-head--center">
        <p class="eyebrow">Nossa loja</p>
        <h2 class="section-title">
          ${f.endereco.cidade ? `Venha nos visitar em ${f.endereco.cidade}.` : "Venha nos visitar."}
        </h2>
        <p class="section-lede">Um espaço pensado para receber você e sua família.</p>
      </div>
      <div class="store">
        ${media}
        <div class="store-cards">
          ${linhas.length
        ? html `<div class="info-card">
                <span class="chip">${iconPin}</span>
                <p class="eyebrow">Endereço</p>
                <address>${linhas.map((l, i) => (i ? html `<br />${l}` : html `${l}`))}</address>
                <a class="info-link" href="${safeUrl(mapsLinkUrl(f))}" rel="noopener" target="_blank">
                  Ver no mapa${iconArrow}
                </a>
              </div>`
        : EMPTY}
          ${cardHorario}
          ${waHref
        ? html `<div class="info-card">
                <span class="chip chip--wa">${iconWhatsapp}</span>
                <p class="eyebrow">WhatsApp</p>
                <a class="big big--wa" href="${safeUrl(waHref)}" rel="noopener" target="_blank" style="text-decoration:none">
                  ${formatPhoneBR(f.whatsapp)}
                </a>
                <small>Clique para falar com um atendente.</small>
              </div>`
        : EMPTY}
        </div>
      </div>
    </div>
  </section>`;
}
function ctaBanner(f, name, waHref) {
    const hasPhone = normalizePhoneBR(f.telefoneComercial) !== null;
    if (!waHref && !hasPhone)
        return EMPTY;
    const acao = waHref
        ? html `<a class="btn btn--on-brand" href="${safeUrl(waHref)}" rel="noopener" target="_blank">${iconWhatsapp}Conversar pelo WhatsApp</a>`
        : html `<a class="btn btn--on-brand" href="${safeUrl(telLink(f.telefoneComercial))}">${iconPhone}Ligar para a ${name}</a>`;
    return html `<section class="section" id="contato">
    <div class="container">
      <div class="cta-banner">
        <h2>Precisa de medicamentos, higiene ou atendimento farmacêutico?</h2>
        <p>Entre em contato com nossa equipe. Será um prazer atender você.</p>
        ${acao}
      </div>
    </div>
  </section>`;
}
function instagramUrl(handle) {
    const h = handle.trim();
    if (!h)
        return null;
    if (/^https?:\/\//i.test(h))
        return h;
    return `https://instagram.com/${h.replace(/^@/, "")}`;
}
function footer(f, name, waHref, principal) {
    const logoUrl = f.logo?.url ?? "";
    const linhas = addressLines(f);
    const ig = instagramUrl(f.instagram);
    const bio = f.descricaoCurta || f.sobre.split(/\n\s*\n/)[0] || "";
    const contatos = [];
    if (linhas.length)
        contatos.push(html `<li>${iconPin}<span>${addressOneLine(f)}</span></li>`);
    if (normalizePhoneBR(f.telefoneComercial)) {
        contatos.push(html `<li>${iconPhone}<a href="${safeUrl(telLink(f.telefoneComercial))}">${formatPhoneBR(f.telefoneComercial)}</a></li>`);
    }
    if (waHref) {
        contatos.push(html `<li>${iconWhatsapp}<a href="${safeUrl(waHref)}" rel="noopener" target="_blank">WhatsApp ${formatPhoneBR(f.whatsapp)}</a></li>`);
    }
    if (f.email)
        contatos.push(html `<li>${iconMail}<a href="${safeUrl(`mailto:${f.email}`)}">${f.email}</a></li>`);
    if (ig) {
        contatos.push(html `<li>${iconInstagram}<a href="${safeUrl(ig)}" rel="noopener" target="_blank">${f.instagram}</a></li>`);
    }
    if (principal) {
        contatos.push(html `<li>${iconClock}<span>${principal.label} · ${principal.longo}</span></li>`);
    }
    return html `<footer class="site-footer">
    <div class="container">
      <div class="footer-grid">
        <div class="footer-brand">
          ${logoUrl
        ? html `<img src="${safeUrl(logoUrl)}" alt="Logo ${name}" width="140" height="40" loading="lazy" />`
        : html `<p class="name">${name}</p>`}
          ${bio ? html `<p>${bio}</p>` : EMPTY}
        </div>

        ${contatos.length
        ? html `<div>
              <h3>Contato</h3>
              <ul class="footer-list">${contatos}</ul>
            </div>`
        : EMPTY}

        <div class="footer-company">
          <h3>Empresa</h3>
          ${f.razaoSocial ? html `<p>${f.razaoSocial}</p>` : EMPTY}
          ${f.cnpjs.map((cnpj) => html `<p>CNPJ ${cnpj}</p>`)}
          ${f.farmaceuticos.length
        ? html `<div class="rt">
                <span>${f.farmaceuticos.length > 1 ? "Responsáveis técnicos" : "Responsável técnico(a)"}</span>
                ${f.farmaceuticos.map((rt) => html `<p class="rt-item">
                    <b>${rt.nome}</b>${rt.crf ? html ` — ${rt.crf}` : EMPTY}
                  </p>`)}
              </div>`
        : EMPTY}
        </div>
      </div>

      <div class="footer-legal">
        ${f.licencaSanitaria ? html `<p>Licença sanitária / AFE: ${f.licencaSanitaria}</p>` : EMPTY}
        <p>
          As informações desta página têm caráter institucional e não substituem a orientação de um
          profissional de saúde. Em caso de dúvidas sobre medicamentos, procure um farmacêutico ou médico.
        </p>
        <p>Este site é informativo e não realiza venda de medicamentos.</p>
        <p>© ${new Date().getFullYear()} ${name}. Todos os direitos reservados.</p>
      </div>

      <p class="footer-by">
        Desenvolvido por <a href="https://oniochat.com.br" rel="noopener" target="_blank">Onio Chat</a>
      </p>
    </div>
  </footer>`;
}
/* ------------------------------------------------------------------ */
/** Renderiza o documento HTML completo da landing de uma farmácia publicada. */
export function renderLandingHTML({ farmacia: f, baseUrl }) {
    const name = f.nomeFantasia || f.razaoSocial || "Farmácia";
    const waMsg = `Olá! Vim pelo site da ${name} e gostaria de fazer um pedido.`;
    const waHref = waLink(f.whatsapp, waMsg);
    const desc = metaDescription(f, name);
    const jsonLd = buildPharmacyJsonLd(f, baseUrl);
    const ogImage = f.fotoHero?.url || f.fotoFachada?.url || f.logo?.url || "";
    const logoUrl = f.logo?.url ?? "";
    // Horários são derivados uma vez só: hero, "por que escolher", loja e rodapé
    // leem o mesmo resultado, em vez de cada um recalcular por conta.
    const grupos = gruposHorario(f);
    const principal = grupoPrincipal(grupos);
    const secoes = new Set();
    if (f.sobre || f.descricaoCurta)
        secoes.add("sobre");
    if (f.servicos.length)
        secoes.add("servicos");
    if (waHref || normalizePhoneBR(f.telefoneComercial))
        secoes.add("contato");
    const body = html `${header(f, name, waHref, secoes)}
    <main>
      ${hero(f, name, waHref, principal)}
      ${sobreSection(f, name)}
      ${servicosSection(f)}
      ${porQueSection(f, name, waHref, principal)}
      ${lojaSection(f, name, waHref, grupos, principal)}
      ${ctaBanner(f, name, waHref)}
    </main>
    ${footer(f, name, waHref, principal)}
    ${waHref
        ? html `<a class="fab" href="${safeUrl(waHref)}" rel="noopener" target="_blank" aria-label="Falar no WhatsApp">${iconWhatsapp}</a>`
        : EMPTY}`;
    return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(name)}${f.endereco.cidade ? ` — ${escapeHtml(f.endereco.cidade)}` : ""}</title>
<meta name="description" content="${escapeHtml(desc)}" />
<link rel="canonical" href="${escapeHtml(baseUrl)}" />
<meta property="og:type" content="website" />
<meta property="og:site_name" content="${escapeHtml(name)}" />
<meta property="og:locale" content="pt_BR" />
<meta property="og:title" content="${escapeHtml(name)}" />
<meta property="og:description" content="${escapeHtml(desc)}" />
<meta property="og:url" content="${escapeHtml(baseUrl)}" />
${ogImage ? `<meta property="og:image" content="${escapeHtml(ogImage)}" />` : ""}
<meta property="fb:app_id" content="${FB_APP_ID}" />
<meta name="twitter:card" content="${ogImage ? "summary_large_image" : "summary"}" />
<meta name="author" content="${AUTHOR}" />
<meta name="robots" content="index,follow" />
${logoUrl ? `<link rel="icon" href="${escapeHtml(logoUrl)}" />` : ""}
${renderStyles(f.theme?.cor)}
<script type="application/ld+json">${jsonLd.replace(/</g, "\\u003c")}</script>
</head>
<body>
${body}
</body>
</html>`;
}
//# sourceMappingURL=render.js.map